import React from 'react';
import { FontWeights, getTheme, mergeStyleSets, Modal, PrimaryButton } from '@fluentui/react';

interface EnvelopeInfoModalProps {
    isOpen: boolean;
    envelopeId: string | null;
    finalPrice: number | null;
    isCloseButtonDisabled: boolean;
    closeCountdown: number;
    onDismiss: () => void;
}

const EnvelopeInfoModal: React.FC<EnvelopeInfoModalProps> = ({
    isOpen,
    envelopeId,
    finalPrice,
    isCloseButtonDisabled,
    closeCountdown,
    onDismiss,
}) => {
    const theme = getTheme();
    const styles = mergeStyleSets({
        header: [
            {
                flex: '1 1 auto',
                borderTop: `4px solid ${theme.palette.themePrimary}`,
                color: theme.palette.neutralPrimary,
                display: 'flex',
                alignItems: 'center',
                fontWeight: FontWeights.semibold,
                padding: '12px 12px 14px 24px',
            },
        ],
        heading: {
            color: theme.palette.neutralPrimary,
            fontWeight: FontWeights.semibold,
            fontSize: 'inherit',
            margin: '0',
        },
        body: {
            flex: '4 4 auto',
            padding: '0 24px 24px 24px',
            overflowY: 'hidden',
            selectors: {
                p: { margin: '14px 0' },
                'p:first-child': { marginTop: 0 },
                'p:last-child': { marginBottom: 0 },
                '@media (max-width: 640px)': {
                    padding: '0 16px 60px 16px',
                },
            },
        },
        envelopeSection: {
            marginBottom: '20px',
        },
        envelopeCard: {
            borderRadius: '12px',
            padding: '16px 24px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            background: '#e3f2fd',
            border: '2px solid #42a5f5',
        },
        envelopeNumber: {
            fontSize: '48px',
            fontWeight: FontWeights.bold,
            color: '#1565c0',
            lineHeight: '1',
            letterSpacing: '-1px',
            display: 'block',
            selectors: {
                '@media (max-width: 480px)': {
                    fontSize: '36px',
                },
            },
        },
        amountCard: {
            borderRadius: '12px',
            padding: '16px 24px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
        },
        amountCardPositive: {
            background: '#e8f5e9',
            border: '2px solid #66bb6a',
        },
        amountCardNegative: {
            background: '#e3f2fd',
            border: '2px solid #66bb6a',
        },
        amountIcon: {
            fontSize: '32px',
            flexShrink: 0,
        },
        amountTextGroup: {
            flex: 1,
        },
        amountSubLabel: {
            fontSize: '12px',
            fontWeight: FontWeights.semibold,
            color: theme.palette.neutralSecondary,
            textTransform: 'uppercase' as const,
            letterSpacing: '1px',
            display: 'block',
            marginBottom: '2px',
        },
        amountValue: {
            fontSize: '32px',
            fontWeight: FontWeights.bold,
            color: theme.palette.neutralDark,
            display: 'block',
            selectors: {
                '@media (max-width: 480px)': {
                    fontSize: '26px',
                },
            },
        },
        stepsList: {
            listStyle: 'none',
            margin: '0 0 20px 0',
            padding: '0',
            display: 'flex',
            flexDirection: 'column' as const,
            gap: '10px',
        },
        stepItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            background: theme.palette.neutralLighterAlt,
            borderRadius: '10px',
            padding: '12px 16px',
            border: `1px solid ${theme.palette.neutralQuaternaryAlt}`,
        },
        stepNumber: {
            background: theme.palette.themePrimary,
            color: theme.palette.white,
            borderRadius: '50%',
            width: '28px',
            height: '28px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: FontWeights.bold,
            flexShrink: 0,
        },
        stepText: {
            fontSize: '15px',
            color: theme.palette.neutralDark,
            lineHeight: '1.4',
        },
        stepHighlight: {
            fontWeight: FontWeights.bold,
            color: theme.palette.themePrimary,
        },
        zeroAmountMessage: {
            textAlign: 'center',
            fontSize: '16px',
            color: theme.palette.neutralSecondary,
            marginBottom: '20px',
        },
        centeredContainer: {
            textAlign: 'center',
            marginTop: '24px',
        },
        primaryActionButton: {
            width: '100%',
            height: '48px',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: FontWeights.semibold,
        },
    });

    const price = finalPrice ?? 0;
    const roundedPrice = Math.round(price);

    return (
        <Modal
            isOpen={isOpen}
            isBlocking={true}
            onDismiss={onDismiss}
            styles={{
                main: {
                    width: '90%',
                    maxWidth: '600px',
                    borderRadius: '12px',
                    padding: '0',
                    overflow: 'hidden',
                },
            }}
        >
            <div className={styles.header}>
                <h2 className={styles.heading}>
                    ✅ Jízda byla úspěšně zaznamenána
                </h2>
            </div>
            <div className={styles.body}>

                {/* Envelope number — always shown */}
                <div className={styles.envelopeSection}>
                    <div className={styles.envelopeCard}>
                        <span className={styles.amountIcon}>📩</span>
                        <div className={styles.amountTextGroup}>
                            <span className={styles.amountSubLabel}>Číslo obálky</span>
                            <span className={styles.envelopeNumber}>{envelopeId}</span>
                        </div>
                    </div>
                </div>

                {price > 0 && (
                    <>
                        {/* Amount card — positive: driver pays into envelope */}
                        <div className={`${styles.amountCard} ${styles.amountCardPositive}`}>
                            <span className={styles.amountIcon}>💵</span>
                            <div className={styles.amountTextGroup}>
                                <span className={styles.amountSubLabel}>Vložit do obálky</span>
                                <span className={styles.amountValue}>
                                    {roundedPrice.toLocaleString('cs-CZ')} Kč
                                </span>
                            </div>
                        </div>

                        {/* Action steps */}
                        <ol className={styles.stepsList}>
                            <li className={styles.stepItem}>
                                <span className={styles.stepNumber}>1</span>
                                <span className={styles.stepText}>Vezmi prázdnou obálku</span>
                            </li>
                            <li className={styles.stepItem}>
                                <span className={styles.stepNumber}>2</span>
                                <span className={styles.stepText}>
                                    Vlož do ní{' '}
                                    <span className={styles.stepHighlight}>
                                        {roundedPrice.toLocaleString('cs-CZ')} Kč
                                    </span>
                                </span>
                            </li>
                            <li className={styles.stepItem}>
                                <span className={styles.stepNumber}>3</span>
                                <span className={styles.stepText}>
                                    Napiš na obálku číslo{' '}
                                    <span className={styles.stepHighlight}>{envelopeId}</span>
                                </span>
                            </li>
                        </ol>
                    </>
                )}

                {price < 0 && (
                    /* Amount card — negative: driver gets money back */
                    <div className={`${styles.amountCard} ${styles.amountCardNegative}`}>
                        <span className={styles.amountIcon}>💰</span>
                        <div className={styles.amountTextGroup}>
                            <span className={styles.amountSubLabel}>Dostaneš</span>
                            <span className={styles.amountValue}>
                                {Math.abs(roundedPrice).toLocaleString('cs-CZ')} Kč
                            </span>
                        </div>
                    </div>
                )}

                {price === 0 && (
                    <p className={styles.zeroAmountMessage}>
                        Nic víc dělat nemusíš.
                    </p>
                )}

                <div className={styles.centeredContainer}>
                    <PrimaryButton
                        onClick={onDismiss}
                        disabled={isCloseButtonDisabled}
                        styles={{ root: styles.primaryActionButton }}
                        iconProps={{ iconName: 'CheckMark' }}
                    >
                        {isCloseButtonDisabled ? `Zavřít (${closeCountdown}s)` : 'Zavřít'}
                    </PrimaryButton>
                </div>
            </div>
        </Modal>
    );
};

export default EnvelopeInfoModal;
